import type {CohortGroupEntry} from 'common/query-builder/query-entry-section/types';
import type {SerializedSankeySegment} from 'common/widgets/sankey-chart/models/serialized';
import type {FlowsBookmark} from 'common/report/flows/models/types';
import type {FunnelBookmark} from 'common/report/funnels/models/types';
import {BaseDate, InsightsBookmarkParams} from 'common/types/reports/bookmark';

import {BehaviorCountType, WindowUnit} from '../enums/expression-enums';
import type {Expression} from './expression-types';

export interface Serializable {
  serialize(): Obj;
}

/*
  TODO: Implement union type for underlying cohort data
  Cohorts can be queried with (selectors and behaviors) OR (groups).
  (selectors and behaviors) are currently only used in our messaged products.
  We need to maintain support for the "Old Format" until Messages is fully deprecated.
  type OldFormat = {
    selector: Expression;
    behaviors: SerializedBehaviorDictionary;
  },
  type NewFormat = {
    groups: CohortGroupEntry
  };
  type BaseFormat = OldFormat | NewFormat
*/
export interface SerializedCohort {
  active_integrations?: string[];
  behaviors?: SerializedBehaviorDictionary;
  count?: number;
  created_by?: User;
  referenced_by?: number[];
  referenced_by_campaigns?: number[];
  description?: string;
  id?: number;
  unsavedId?: string;
  last_edited?: string;
  last_queried?: string;
  name?: string;
  selector?: Expression;
  // data_group_id must be a string because it can be bigger than Number.MAX_SAFE_INTEGER
  data_group_id?: string;

  // new cohorts format
  groups?: CohortGroupEntry[];

  // permissions
  can_view?: boolean;
  can_update_basic?: boolean;
  can_share?: boolean;
  is_superadmin?: boolean;
  allow_staff_override?: boolean;
}

export interface SerializedInlineCohort {
  id?: number; // SerializedInlineCohorts may have an id if they have been saved
  created_by?: User;
  name?: string;
  raw_cohort: {
    unsavedId?: string;
    behaviors: SerializedBehaviorDictionary;
    selector: Expression;
    // data_group_id must be a string because it can be bigger than Number.MAX_SAFE_INTEGER
    data_group_id?: string;
  };
}

export const isSerializedInlineCohort = (
  cohort: SerializedCohort | SerializedInlineCohort,
): cohort is SerializedInlineCohort => {
  return cohort && (cohort as SerializedInlineCohort).raw_cohort !== undefined;
};

export interface User {
  name: string;
  email: string;
  id: number;
}

export interface SerializedBehaviorDictionary {
  [behaviorName: string]: SerializedBehavior;
}

export enum ReportBasedCohorts {
  Addiction = `addiction_report`,
  Flows = `flows_report`,
  Funnels = `funnel_report`,
  Retention = `retention_report`,
}

export type SerializedBehavior = {
  count?: SerializedBehaviorCount;
  funnel?: SerializedFunnel;
  from_date?: BaseDate;
  to_date?: BaseDate;
  window?: SerializedBehaviorWindow;
} & Partial<SerializedReportBehaviorWrapper>;

export type SerializedReportBehaviorWrapper =
  | SerializedAddictionReportWrapper
  | SerializedFlowsReportWrapper
  | SerializedFunnelReportWrapper
  | SerializedRetentionReportWrapper;

export interface SerializedBehaviorCount {
  event_selector: SerializedEventSelector;
  type: BehaviorCountType;
}

export interface SerializedEventSelector {
  event: string;
  selector?: Expression;
}

export interface SerializedBehaviorWindow {
  unit: WindowUnit;
  value: number;
}

export interface SerializedDatetime {
  from_date?: string | SerializedBehaviorWindow;
  to_date?: string | SerializedBehaviorWindow;
  window?: SerializedBehaviorWindow;
}

// Addiction
export type SerializedAddictionReportWrapper = {
  [ReportBasedCohorts.Addiction]: SerializedAddictionReport;
};

export interface SerializedAddictionReport {
  bookmark: InsightsBookmarkParams; // This should be reusable for this case
  // one of behavior_params/average_behavior_params should be specified
  behavior_params?: SerializedAddictionReportBehaviorParam[];
  average_behavior_params?: SerializedAddictionReportBehaviorParam[];
  cumulative?: boolean;
}

export type SerializedAddictionReportBehaviorParam =
  | SerializedAddictionReportRetainedBehavior
  | SerializedAddictionReportDropOffBehavior;

export interface SerializedAddictionReportBehavior {
  addiction_index: number;
  addiction_interval_index?: number;
}

export interface SerializedAddictionReportRetainedBehavior {
  addicted_behavior: SerializedAddictionReportBehavior;
  segments: string[];
}

export interface SerializedAddictionReportDropOffBehavior {
  drop_off_behavior: SerializedAddictionReportBehavior;
  segments: string[];
}
// Flows
export type SerializedFlowsReportWrapper = {
  [ReportBasedCohorts.Flows]: SerializedFlowsReport;
};

export interface SerializedFlowsReport {
  bookmark: FlowsBookmark;
  behavior_params: SerializedFlowsReportBehaviorParam[];
}

export type SerializedFlowsReportBehaviorParam =
  | SerializedFlowsReportEventBehaviorParam
  | SerializedFlowsReportOtherEventsBehaviorParam
  | SerializedFlowsReportDropOffBehaviorParam;

export interface SerializedFlowsBreakdown {
  segments: SerializedSankeySegment[];
}

export interface SerializedFlowsReportEventBehavior {
  event: string;
  step_number: number;
  is_computed?: boolean;
  segments?: SerializedSankeySegment[];
  breakdowns?: SerializedFlowsBreakdown[];
  prior_anchor_step_number?: number;
  number_of_forward_steps_since_prior_anchor?: number;
  is_relative_forward_anchor?: boolean;
}

export interface SerializedFlowsReportEventBehaviorParam {
  event_behavior: SerializedFlowsReportEventBehavior;
}

export interface SerializedFlowsReportOtherEventsBehaviorEvent {
  event: string;
  is_computed?: boolean;
  segments?: SerializedSankeySegment[];
}

export interface SerializedFlowsReportOtherEventsBehavior {
  step_number: number;
  top_events: SerializedFlowsReportOtherEventsBehaviorEvent[];
  breakdowns?: SerializedFlowsBreakdown[];
}

export interface SerializedFlowsReportOtherEventsBehaviorParam {
  other_events_behavior: SerializedFlowsReportOtherEventsBehavior;
}

export interface SerializedFlowsReportDropOffBehavior {
  step_number: number;
}

export interface SerializedFlowsReportDropOffBehaviorParam {
  drop_off_behavior: SerializedFlowsReportDropOffBehavior;
}

// Funnels
export type SerializedFunnelReportWrapper = {
  [ReportBasedCohorts.Funnels]: SerializedFunnelReport;
};

export interface SerializedFunnelReport {
  bookmark: FunnelBookmark;
  behavior_params: SerializedFunnelReportBehaviorParam[];
}

export type SerializedFunnelReportBehaviorParam =
  | SerializedFunnelReportConvertBehavior
  | SerializedFunnelReportDropOffBehavior
  | SerializedFunnelReportTimeBucketBehavior;

export interface SerializedFunnelReportStep {
  step: number;
}

export interface SerializedFunnelReportTimeBucketBehaviorStep {
  step: number;
  bucket_index: number;
  from_start: boolean;
}

export interface SerializedFunnelReportConvertBehavior {
  convert_behavior: SerializedFunnelReportStep;
}

export interface SerializedFunnelReportDropOffBehavior {
  drop_off_behavior: SerializedFunnelReportStep;
}

export interface SerializedFunnelReportTimeBucketBehavior {
  time_bucket_behavior: SerializedFunnelReportTimeBucketBehaviorStep;
}

export interface SerializedFunnel {
  steps: SerializedFunnelStep[];
}

export interface SerializedFunnelStep {
  count: SerializedBehaviorCount;
  value: number;
}

// Retention
export type SerializedRetentionReportWrapper = {
  [ReportBasedCohorts.Retention]: SerializedRetentionReport;
};

export interface SerializedRetentionReport {
  bookmark: InsightsBookmarkParams;
  // one of behavior_params/average_behavior_params should be specified
  behavior_params?: SerializedRetentionReportBehaviorParam[];
  average_behavior_params?: SerializedRetentionReportBehaviorParam[];
}

export type SerializedRetentionReportBehaviorParam =
  | SerializedRetentionReportRetainedBehavior
  | SerializedRetentionReportDropOffBehavior;

export interface SerializedRetentionReportBehavior {
  // Birth interval applies when creating cohort from a date row but not when creating cohort from average row
  birth_interval_index?: number;
  retained_interval_index: number;
}

export interface SerializedRetentionReportRetainedBehavior {
  retained_behavior: SerializedRetentionReportBehavior;
  segments: string[];
}

export interface SerializedRetentionReportDropOffBehavior {
  drop_off_behavior: SerializedRetentionReportBehavior;
  segments: string[];
}
